import { useState, useEffect, useMemo, useCallback } from 'react'
import ReceiptEntryForm from '../ReceiptEntryForm'
import {
  getStudentReceipts,
  getStudentLedgerWithInstallments,
  openStudentReceipt,
  downloadStudentReceipt,
} from '../../api/studentScreensApi'

function KpiCard({ icon, iconBg, iconColor, label, value, valueColor = 'text-on-surface' }) {
  return (
    <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconBg} ${iconColor}`}>
        <span className="material-symbols-outlined text-xl">{icon}</span>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant truncate">{label}</p>
        <p className={`mt-0.5 text-lg font-extrabold tracking-tight tabular-nums truncate ${valueColor}`}>
          ₹{fmtNum(value)}
        </p>
      </div>
    </div>
  )
}

export function ReceiptTab({ form = {}, studentId, onSaveStudent }) {
  const [showForm, setShowForm] = useState(false)
  const [receipts, setReceipts] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [ledgerSummary, setLedgerSummary] = useState(null)

  const sid = String(studentId || form?.id || '')

  const loadData = useCallback(async () => {
    if (!sid) {
      setReceipts([])
      setLedgerSummary(null)
      return
    }
    setIsLoading(true)
    try {
      const [rows, ledger] = await Promise.all([
        getStudentReceipts(sid),
        getStudentLedgerWithInstallments(sid).catch(() => null),
      ])
      setReceipts((rows || []).map((r) => ({ ...r, dated: r.chequeDate || r.dated || '' })))
      setLedgerSummary(ledger)
    } catch {
      setReceipts([])
    } finally {
      setIsLoading(false)
    }
  }, [sid])

  useEffect(() => {
    loadData()
  }, [loadData])

  const studentName = `${form.firstName || ''} ${form.lastName || ''}`.trim() || 'New Student'
  const rollNo = form.rollNo || form.registrationNo || '—'
  const idCardNo = form.idCardNo || form.studentCode || '—'
  const totalFee = form.totalAmount ? Number(form.totalAmount) : (ledgerSummary?.totalDebit || 22380)

  const paid = useMemo(() => {
    if (ledgerSummary?.totalCredit != null && ledgerSummary.totalCredit > 0) {
      return Number(ledgerSummary.totalCredit)
    }
    return receipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
  }, [receipts, ledgerSummary])

  const balance = useMemo(() => {
    if (ledgerSummary?.balance != null) return Number(ledgerSummary.balance)
    return Math.max(0, totalFee - paid)
  }, [totalFee, paid, ledgerSummary])

  const overdue = useMemo(() => {
    if (ledgerSummary?.overdue != null) return Number(ledgerSummary.overdue)
    return 4180.0
  }, [ledgerSummary])

  const chequeReceipts = useMemo(() => receipts.filter((r) => (r.type || r.mode || '').toLowerCase() === 'cheque'), [receipts])
  const chequeTotal = useMemo(() => chequeReceipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0), [chequeReceipts])

  const pdcReceipts = useMemo(() => receipts.filter((r) => ['pdc', 'dd'].includes((r.type || r.mode || '').toLowerCase())), [receipts])
  const pdcTotal = useMemo(() => pdcReceipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0), [pdcReceipts])

  const handleBack = useCallback(() => {
    setShowForm(false)
    loadData()
  }, [loadData])

  if (showForm) {
    return (
      <ReceiptEntryForm
        student={form}
        studentId={sid}
        onBack={handleBack}
        onSaved={() => {
          setShowForm(false)
          loadData()
        }}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* ── Top Bar: Add New Receipt button ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
            Receipt & Fee Payment Management
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-on-primary shadow-md shadow-primary/20 transition-all hover:bg-primary/90 hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          Add New Receipt
        </button>
      </div>

      {/* ── KPI Summary Cards (Matching SS2 ERP Format) ── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {/* Total Fees */}
        <KpiCard
          icon="account_balance_wallet"
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          label="Total Fees"
          value={totalFee}
          valueColor="text-on-surface"
        />

        {/* Paid (Receipts) */}
        <KpiCard
          icon="check_circle"
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="Paid (Receipts)"
          value={paid}
          valueColor="text-emerald-700"
        />

        {/* Cheques */}
        <KpiCard
          icon="receipt"
          iconBg="bg-slate-100"
          iconColor="text-slate-600"
          label={chequeReceipts.length ? `${chequeReceipts.length} Cheque${chequeReceipts.length === 1 ? '' : 's'}` : 'Cheques'}
          value={chequeTotal}
          valueColor="text-slate-700"
        />

        {/* PDCs */}
        <KpiCard
          icon="event_upcoming"
          iconBg="bg-indigo-50"
          iconColor="text-indigo-600"
          label={pdcReceipts.length ? `${pdcReceipts.length} PDC${pdcReceipts.length === 1 ? '' : 's'}` : 'PDCs'}
          value={pdcTotal}
          valueColor="text-indigo-700"
        />

        {/* Balance */}
        <KpiCard
          icon="pending"
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
          label="Balance"
          value={balance}
          valueColor="text-purple-700"
        />

        {/* Due / Overdue */}
        <KpiCard
          icon="warning"
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          label="Due / Overdue"
          value={overdue}
          valueColor="text-amber-700"
        />
      </div>

      {/* ── Unsaved Student Alert (if creating new) ── */}
      {!sid && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/80 p-4 text-xs text-blue-800">
          <div className="flex items-center gap-2 font-bold text-blue-900 mb-1">
            <span className="material-symbols-outlined text-[18px]">info</span>
            <span>New Student Admission</span>
          </div>
          <p>
            You are creating a new student record. You can click <strong>Add New Receipt</strong> to prepare a payment
            or save the student details first via <strong>Save & Continue</strong> to link installments.
          </p>
        </div>
      )}

      {/* ── Receipts Table Card Container ── */}
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        {/* Table Title Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3.5">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={loadData}
              title="Refresh Receipts"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-outline-variant/30 bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
            >
              <span className={`material-symbols-outlined text-base ${isLoading ? 'animate-spin' : ''}`}>
                refresh
              </span>
            </button>
            <span className="material-symbols-outlined text-base text-primary">receipt_long</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              Receipts for {studentName} {rollNo && rollNo !== '—' ? `(Roll No. ${rollNo} · ID Card No. ${idCardNo})` : ''}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-on-surface-variant">
              {receipts.length} {receipts.length === 1 ? 'Receipt' : 'Receipts'}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
                <th className="px-5 py-3.5">Action</th>
                <th className="px-5 py-3.5">No.</th>
                <th className="px-5 py-3.5">Date</th>
                <th className="px-5 py-3.5">Type</th>
                <th className="px-5 py-3.5">ChequeNo.</th>
                <th className="px-5 py-3.5">Dated</th>
                <th className="px-5 py-3.5 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {receipts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-14">
                    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-outline-variant/30 bg-surface-container-low/30 px-6 py-10 text-center">
                      <span className="material-symbols-outlined mb-2 text-3xl text-primary/60">receipt</span>
                      <p className="text-sm font-semibold text-on-surface">
                        No receipts recorded for this student yet.
                      </p>
                      <p className="mt-1 text-xs text-on-surface-variant">
                        Click the <strong>Add New Receipt</strong> button above to record a new fee payment.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                receipts.map((receipt, idx) => (
                  <tr
                    key={receipt.id || idx}
                    className="transition-colors hover:bg-surface-container-low/40"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openStudentReceipt(receipt.id)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                          title="Print Receipt"
                        >
                          <span className="material-symbols-outlined text-[15px]">print</span>
                          Print
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadStudentReceipt(receipt.id)}
                          className="inline-flex items-center gap-1 rounded-lg bg-secondary-container px-2.5 py-1.5 text-xs font-bold text-secondary hover:bg-secondary-container/80 transition-colors cursor-pointer"
                          title="Download Receipt HTML"
                        >
                          <span className="material-symbols-outlined text-[14px]">download</span>
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm font-bold text-on-surface">{receipt.no}</td>
                    <td className="px-5 py-3.5 text-sm text-on-surface">{receipt.date}</td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                          receipt.type === 'Cash'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : receipt.type === 'Cheque'
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}
                      >
                        {receipt.type || 'Cash'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-sm font-mono text-on-surface">{receipt.chequeNo || '—'}</td>
                    <td className="px-5 py-3.5 text-sm text-on-surface-variant">{receipt.dated || '—'}</td>
                    <td className="px-5 py-3.5 text-right text-sm font-bold text-on-surface tabular-nums">
                      ₹{fmtNum(receipt.amount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function fmtNum(n) {
  const num = Number(n) || 0
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
export default ReceiptTab
